/**
 * Sidebar window custom-event wiring, consolidating the five scattered
 * `window.addEventListener` / `addWindowListener` effects:
 *   1. `openNotifications`            → open the mobile settings modal
 *   2. `openNewModuleMenu`            → open the new-module wizard at step 0
 *   3. `openNewConversationModal`     → create a conversation (mobile header)
 *   4. `setSidebarCollapsed`          → collapse/expand with logo-swap lock
 *   5. `toggleReorderSidebar` /
 *      `openDataBackupsModal`         → toggle drag mode / open backups modal
 *
 * Kept as five distinct `useEffect` calls in the same relative order so each
 * independent listener's mount/cleanup semantics are unchanged.
 */

import { useEffect } from 'react';
import {
  addWindowListener,
  conversationEvents,
  removeWindowListener,
} from '@/lib/conversation-events';

interface UseSidebarWindowEventsArgs {
  isMobile: boolean;
  handleCreateConversation: () => void;
  lockLogoSwapDuringCollapse: () => void;
  clearCollapseLogoSwapLock: () => void;
  setIsMobileSettingsOpen: (value: boolean) => void;
  setIsNewModuleMenuOpen: (value: boolean) => void;
  setNewModuleStep: (value: { type: string; label: string; emoji: string } | null) => void;
  setNewModuleName: (value: string) => void;
  setNewModuleNameError: (value: string) => void;
  setIsCollapsed: (value: boolean) => void;
  setIsDragMode: (value: boolean | ((prev: boolean) => boolean)) => void;
  setContextMenu: (value: null) => void;
  setIsDataBackupsModalOpen: (value: boolean) => void;
}

export function useSidebarWindowEvents({
  isMobile,
  handleCreateConversation,
  lockLogoSwapDuringCollapse,
  clearCollapseLogoSwapLock,
  setIsMobileSettingsOpen,
  setIsNewModuleMenuOpen,
  setNewModuleStep,
  setNewModuleName,
  setNewModuleNameError,
  setIsCollapsed,
  setIsDragMode,
  setContextMenu,
  setIsDataBackupsModalOpen,
}: UseSidebarWindowEventsArgs): void {
  useEffect(() => {
    const handler = () => setIsMobileSettingsOpen(true);
    window.addEventListener('openNotifications', handler);
    return () => window.removeEventListener('openNotifications', handler);
  }, []);

  useEffect(() => {
    const handler = () => {
      setIsNewModuleMenuOpen(true);
      setNewModuleStep(null);
      setNewModuleName('');
      setNewModuleNameError('');
    };
    window.addEventListener('openNewModuleMenu', handler);
    return () => window.removeEventListener('openNewModuleMenu', handler);
  }, []);

  useEffect(() => {
    const handleOpenNewConversationModal = () => {
      handleCreateConversation();
    };
    addWindowListener(conversationEvents.openNewConversationModal, handleOpenNewConversationModal);
    return () => {
      removeWindowListener(conversationEvents.openNewConversationModal, handleOpenNewConversationModal);
    };
  }, [handleCreateConversation]);

  useEffect(() => {
    const handleSetSidebarCollapsed = (event: Event) => {
      if (isMobile) return;
      const detail = (event as CustomEvent).detail as { collapsed?: boolean } | undefined;
      if (typeof detail?.collapsed !== 'boolean') return;
      if (detail.collapsed) {
        lockLogoSwapDuringCollapse();
      } else {
        clearCollapseLogoSwapLock();
      }
      setIsCollapsed(detail.collapsed);
    };

    addWindowListener(conversationEvents.setSidebarCollapsed, handleSetSidebarCollapsed as EventListener);
    return () => {
      removeWindowListener(conversationEvents.setSidebarCollapsed, handleSetSidebarCollapsed as EventListener);
    };
  }, [clearCollapseLogoSwapLock, isMobile, lockLogoSwapDuringCollapse]);

  useEffect(() => {
    const handleToggleReorder = () => {
      setIsDragMode(prev => !prev);
      setContextMenu(null);
    };
    const handleOpenDataBackups = () => setIsDataBackupsModalOpen(true);

    window.addEventListener('toggleReorderSidebar', handleToggleReorder);
    window.addEventListener('openDataBackupsModal', handleOpenDataBackups);

    return () => {
      window.removeEventListener('toggleReorderSidebar', handleToggleReorder);
      window.removeEventListener('openDataBackupsModal', handleOpenDataBackups);
    };
  }, []);
}
